import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { UserProvider } from "./context/UserContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { NoiseOverlay } from "./components/NoiseOverlay";
import { Header } from "./components/Header";
import { PageTransition } from "./components/PageTransition";
import { 
  ProtectedRoute, 
  FreeMemberRoute, 
  PaidMemberRoute, 
  AdminRoute,
  GuestOnlyRoute 
} from "./components/ProtectedRoute";

// Public Pages
import LandingPage from "./pages/LandingPage";
import InnerCircleSalesPage from "./pages/InnerCircleSalesPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";

// Member Pages
import CommunityDashboard from "./pages/CommunityDashboard";
import StartHerePage from "./pages/StartHerePage";
import ResourceHubPage from "./pages/ResourceHubPage";
import BlueprintPage from "./pages/BlueprintPage";
import CoursesPage from "./pages/CoursesPage";
import ModulePage from "./pages/ModulePage";
import CertificatePage from "./pages/CertificatePage";
import VideoLibraryPage from "./pages/VideoLibraryPage";
import ProfilePage from "./pages/ProfilePage";
import EcosystemToolsPage from "./pages/EcosystemToolsPage";
import InnerCircleDashboard from "./pages/InnerCircleDashboard";
import PremiumCoursePage from "./pages/PremiumCoursePage";

// Admin Pages
import AdminPage from "./pages/AdminPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminSeedingPage from "./pages/AdminSeedingPage";
import CustomPage from "./pages/CustomPage";

// POWR Devotional Pages
import DailyDevotionalPage from "./pages/DailyDevotionalPage";
import DevotionalsPage from "./pages/DevotionalsPage";
import DevotionalStoryPage from "./pages/DevotionalStoryPage";
import LeadersPage from "./pages/LeadersPage";
import GrowthPage from "./pages/GrowthPage";
import LinksResourcesPage from "./pages/LinksResourcesPage";
import FiveMovementPage from "./pages/FiveMovementPage";
import FiveMovementPlatformPage from "./pages/FiveMovementPlatformPage";
import PaperPortfolioPlatformPage from "./pages/PaperPortfolioPlatformPage";
import WealthIntelligencePage from "./pages/WealthIntelligencePage";
import BusinessSuitePage from "./pages/BusinessSuitePage";
import WebsiteGeneratorPage from "./pages/WebsiteGeneratorPage";
import GettingStartedPage from "./pages/GettingStartedPage";
import FreedomPathPage from "./pages/FreedomPathPage";
import MainMenuPage from "./pages/MainMenuPage";

// Conditional Header component - don't show on certain pages
const ConditionalHeader = () => {
  const location = useLocation();
  const hideHeaderPaths = [
    '/', '/landing', '/inner-circle-sales', '/login', '/signup', '/payment-success', '/profile',
    '/community', '/start-here', '/getting-started', '/admin', '/admin-dashboard', '/resources', '/blueprint', '/videos', '/tools',
    '/inner-circle-hub', '/devotionals', '/links', '/5-dollar-movement', '/5-movement-platform', '/paper-portfolio-platform',
    '/business-suite', '/wealth-intelligence', '/website-generator', '/freedom-path', '/hub'
  ];
  const shouldHideHeader = hideHeaderPaths.includes(location.pathname) || 
    location.pathname.startsWith('/community') ||
    location.pathname.startsWith('/page/') ||
    location.pathname.startsWith('/premium-course/') ||
    location.pathname.startsWith('/devotional');
  return shouldHideHeader ? null : <Header />;
};

// Animated Routes wrapper
const AnimatedRoutes = () => {
  return (
    <PageTransition>
      <Routes>
        {/* Public Routes - No Auth Required */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/inner-circle-sales" element={<InnerCircleSalesPage />} />
        <Route path="/payment-success" element={
          <FreeMemberRoute>
            <PaymentSuccessPage />
          </FreeMemberRoute>
        } />
        
        {/* Auth Routes - Guest Only */}
        <Route path="/login" element={
          <GuestOnlyRoute>
            <LoginPage />
          </GuestOnlyRoute>
        } />
        <Route path="/signup" element={
          <GuestOnlyRoute>
            <SignupPage />
          </GuestOnlyRoute>
        } />
        
        {/* Protected Routes - Free Members */}
        <Route path="/hub" element={
          <FreeMemberRoute>
            <MainMenuPage />
          </FreeMemberRoute>
        } />
        <Route path="/community" element={
          <FreeMemberRoute>
            <CommunityDashboard />
          </FreeMemberRoute>
        } />
        <Route path="/start-here" element={
          <FreeMemberRoute>
            <StartHerePage />
          </FreeMemberRoute>
        } />
        
        {/* Getting Started / Onboarding Guide */}
        <Route path="/getting-started" element={
          <FreeMemberRoute>
            <GettingStartedPage />
          </FreeMemberRoute>
        } />
        
        {/* Freedom Path - Financial Freedom System */}
        <Route path="/freedom-path" element={
          <FreeMemberRoute>
            <FreedomPathPage />
          </FreeMemberRoute>
        } />
        
        <Route path="/resources" element={
          <FreeMemberRoute>
            <ResourceHubPage />
          </FreeMemberRoute>
        } />
        
        {/* Links & Resources Page */}
        <Route path="/links" element={
          <FreeMemberRoute>
            <LinksResourcesPage />
          </FreeMemberRoute>
        } />
        
        {/* Investment Tools */}
        <Route path="/5-dollar-movement" element={
          <FreeMemberRoute>
            <FiveMovementPage />
          </FreeMemberRoute>
        } />
        
        {/* $5 Movement Platform Page (Public) */}
        <Route path="/5-movement-platform" element={<FiveMovementPlatformPage />} />
        
        {/* Paper Portfolio Platform Page (Public) */}
        <Route path="/paper-portfolio-platform" element={<PaperPortfolioPlatformPage />} />
        
        {/* Wealth Intelligence Platform Page */}
        <Route path="/wealth-intelligence" element={<WealthIntelligencePage />} />
        
        {/* POWR Business Suite - Inner Circle Exclusive */}
        <Route path="/business-suite" element={
          <PaidMemberRoute>
            <BusinessSuitePage />
          </PaidMemberRoute>
        } />
        
        {/* Website Generator - Admin Only */}
        <Route path="/website-generator" element={
          <AdminRoute>
            <WebsiteGeneratorPage />
          </AdminRoute>
        } />
        
        {/* Course Pages - Free Members can access */}
        <Route path="/courses" element={
          <FreeMemberRoute>
            <CoursesPage />
          </FreeMemberRoute>
        } />
        <Route path="/powr/:slug" element={
          <FreeMemberRoute>
            <ModulePage courseId="powr" />
          </FreeMemberRoute>
        } />
        <Route path="/ai-income/:slug" element={
          <FreeMemberRoute>
            <ModulePage courseId="ai-income" />
          </FreeMemberRoute>
        } />
        
        {/* Premium Course Pages - Paid Members Only */}
        <Route path="/inner-circle/:slug" element={
          <PaidMemberRoute>
            <ModulePage courseId="inner-circle" />
          </PaidMemberRoute>
        } />
        <Route path="/affiliateai/:slug" element={
          <PaidMemberRoute>
            <ModulePage courseId="affiliateai" />
          </PaidMemberRoute>
        } />
        
        {/* Certificate - Protected */}
        <Route path="/certificate" element={
          <FreeMemberRoute>
            <CertificatePage />
          </FreeMemberRoute>
        } />
        <Route path="/certificate/:courseId" element={
          <FreeMemberRoute>
            <CertificatePage />
          </FreeMemberRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } />
        <Route path="/admin-dashboard" element={<AdminDashboardPage />} />
        <Route path="/admin/seeding" element={<AdminSeedingPage />} />
        
        {/* Video Library */}
        <Route path="/videos" element={
          <FreeMemberRoute>
            <VideoLibraryPage />
          </FreeMemberRoute>
        } />
        
        {/* Profile Settings */}
        <Route path="/profile" element={
          <FreeMemberRoute>
            <ProfilePage />
          </FreeMemberRoute>
        } />
        
        {/* Ecosystem Tools - Available to all members but content locked for non-paid */}
        <Route path="/tools" element={
          <FreeMemberRoute>
            <EcosystemToolsPage />
          </FreeMemberRoute>
        } />
        
        {/* Inner Circle Dashboard - Paid Members Only */}
        <Route path="/inner-circle-hub" element={
          <PaidMemberRoute>
            <InnerCircleDashboard />
          </PaidMemberRoute>
        } />
        
        {/* Premium Courses - Paid Members Only */}
        <Route path="/premium-course/:courseId" element={
          <PaidMemberRoute>
            <PremiumCoursePage />
          </PaidMemberRoute>
        } />
        
        {/* Blueprint - Free Members */}
        <Route path="/blueprint" element={
          <FreeMemberRoute>
            <BlueprintPage />
          </FreeMemberRoute>
        } />
        
        {/* POWR Devotional Routes - Public but tracks progress for logged in users */}
        <Route path="/devotionals" element={<DailyDevotionalPage />} />
        <Route path="/devotionals/archive" element={<DevotionalsPage />} />
        <Route path="/devotional/:id" element={<DevotionalStoryPage />} />
        <Route path="/devotionals/leaders" element={<LeadersPage />} />
        <Route path="/devotionals/growth" element={<GrowthPage />} />
        
        {/* Custom Pages */}
        <Route path="/page/:slug" element={<CustomPage />} />
      </Routes>
    </PageTransition>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <UserProvider>
          <BrowserRouter>
            <div className="App min-h-screen bg-background text-foreground">
              <NoiseOverlay />
              <ConditionalHeader />
              <AnimatedRoutes />
              <Toaster 
                position="bottom-right"
                toastOptions={{
                  style: {
                    background: 'hsl(var(--card))',
                    color: 'hsl(var(--foreground))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0px'
                  }
                }}
              />
            </div>
          </BrowserRouter>
        </UserProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
